/**
 * Personal layout of the Workspace sidebar: which entries are hidden, how they are ordered, and
 * any groups or external links the person added. Access control is applied before the layout,
 * so a layout can only hide or rearrange entries the person is already allowed to see.
 *
 * Ids: workspace entries use their registry id (`contacts`), groups use `group:<random>` and
 * links use `link:<random>`.
 */
export type SidebarLayoutGroup = {
  id: string;
  label: string;
  /** Ids of workspace entries and links inside the group, in display order (hidden ones included). */
  children: string[];
};

export type SidebarLayoutLink = {
  id: string;
  label: string;
  url: string;
};

export type SidebarLayout = {
  /** Top-level display order: workspace entry ids, group ids and link ids. Hidden entries keep their slot. */
  order: string[];
  /** Workspace entry and link ids the person chose to hide. */
  hidden: string[];
  groups: SidebarLayoutGroup[];
  links: SidebarLayoutLink[];
};

export const DEFAULT_SIDEBAR_LAYOUT: SidebarLayout = { order: [], hidden: [], groups: [], links: [] };

export const GROUP_ID_PREFIX = 'group:';
export const LINK_ID_PREFIX = 'link:';
export const MAX_GROUP_LABEL_LENGTH = 40;
export const MAX_LINK_LABEL_LENGTH = 40;
export const MAX_LINK_URL_LENGTH = 2048;

export const isGroupId = (id: string): boolean => id.startsWith(GROUP_ID_PREFIX);
export const isLinkId = (id: string): boolean => id.startsWith(LINK_ID_PREFIX);

const STORAGE_PREFIX = 'shahagent.sidebarLayout.v1.';

type LayoutScope = { tenantId: string; userKey: string };

const resolveScope = (
  tenantId?: string | null,
  employeeId?: string | null,
  username?: string | null,
): LayoutScope | null => {
  const tenant = tenantId?.trim().toLowerCase();
  const userKey = (employeeId?.trim() || username?.trim() || '').toLowerCase();
  if (!tenant || !userKey) {
    return null;
  }

  return { tenantId: tenant, userKey };
};

export const sidebarLayoutStorageKey = (tenantId: string, userKey: string): string =>
  `${STORAGE_PREFIX}${tenantId.trim().toLowerCase()}:${userKey.trim().toLowerCase()}`;

const randomId = (prefix: string): string => {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
  return `${prefix}${random}`;
};

const uniqueStrings = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<string>();
  const result: string[] = [];
  for (const entry of value) {
    if (typeof entry === 'string' && entry.length > 0 && !seen.has(entry)) {
      seen.add(entry);
      result.push(entry);
    }
  }

  return result;
};

const normalizeLabel = (value: unknown, maxLength: number): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.slice(0, maxLength) : null;
};

/** Accepts http(s) addresses only; adds `https://` when the scheme is missing. Returns null when unusable. */
export const normalizeLinkUrl = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_LINK_URL_LENGTH) {
    return null;
  }

  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if ((url.protocol !== 'http:' && url.protocol !== 'https:') || !url.hostname) {
      return null;
    }

    return url.toString();
  } catch {
    return null;
  }
};

export const normalizeSidebarLayout = (value: unknown): SidebarLayout => {
  if (!value || typeof value !== 'object') {
    return DEFAULT_SIDEBAR_LAYOUT;
  }

  const candidate = value as { order?: unknown; hidden?: unknown; groups?: unknown; links?: unknown };

  const groups: SidebarLayoutGroup[] = [];
  const groupIds = new Set<string>();
  if (Array.isArray(candidate.groups)) {
    for (const raw of candidate.groups) {
      const group = raw as { id?: unknown; label?: unknown; children?: unknown } | null;
      if (!group || typeof group.id !== 'string' || !isGroupId(group.id) || groupIds.has(group.id)) {
        continue;
      }

      groupIds.add(group.id);
      groups.push({
        id: group.id,
        label: normalizeLabel(group.label, MAX_GROUP_LABEL_LENGTH) ?? '',
        children: uniqueStrings(group.children).filter(id => !isGroupId(id)),
      });
    }
  }

  const links: SidebarLayoutLink[] = [];
  const linkIds = new Set<string>();
  if (Array.isArray(candidate.links)) {
    for (const raw of candidate.links) {
      const link = raw as { id?: unknown; label?: unknown; url?: unknown } | null;
      if (!link || typeof link.id !== 'string' || !isLinkId(link.id) || linkIds.has(link.id)) {
        continue;
      }

      const url = normalizeLinkUrl(link.url);
      if (!url) {
        continue;
      }

      linkIds.add(link.id);
      links.push({ id: link.id, label: normalizeLabel(link.label, MAX_LINK_LABEL_LENGTH) ?? url, url });
    }
  }

  return {
    order: uniqueStrings(candidate.order),
    hidden: uniqueStrings(candidate.hidden).filter(id => !isGroupId(id)),
    groups,
    links,
  };
};

export const isSidebarLayoutCustomized = (layout: SidebarLayout): boolean =>
  layout.order.length > 0 || layout.hidden.length > 0 || layout.groups.length > 0 || layout.links.length > 0;

export const readSidebarLayout = (
  tenantId?: string | null,
  employeeId?: string | null,
  username?: string | null,
): SidebarLayout => {
  const scope = resolveScope(tenantId, employeeId, username);
  if (!scope) {
    return DEFAULT_SIDEBAR_LAYOUT;
  }

  try {
    const raw = localStorage.getItem(sidebarLayoutStorageKey(scope.tenantId, scope.userKey));
    return raw ? normalizeSidebarLayout(JSON.parse(raw)) : DEFAULT_SIDEBAR_LAYOUT;
  } catch {
    return DEFAULT_SIDEBAR_LAYOUT;
  }
};

export const writeSidebarLayout = (
  layout: SidebarLayout,
  tenantId?: string | null,
  employeeId?: string | null,
  username?: string | null,
): void => {
  const scope = resolveScope(tenantId, employeeId, username);
  if (!scope) {
    return;
  }

  try {
    const key = sidebarLayoutStorageKey(scope.tenantId, scope.userKey);
    if (!isSidebarLayoutCustomized(layout)) {
      localStorage.removeItem(key);
      return;
    }

    localStorage.setItem(key, JSON.stringify(layout));
  } catch {
    // The layout still applies for this visit when storage is blocked.
  }
};

// ---------------------------------------------------------------------------------------------
// Resolved tree
// ---------------------------------------------------------------------------------------------

export type SidebarItemEntry<T> = { kind: 'item'; id: string; item: T };
export type SidebarLinkEntry = { kind: 'link'; id: string; link: SidebarLayoutLink };
export type SidebarLeafEntry<T> = SidebarItemEntry<T> | SidebarLinkEntry;
export type SidebarGroupEntry<T> = { kind: 'group'; id: string; group: SidebarLayoutGroup; children: SidebarLeafEntry<T>[] };
export type SidebarEntry<T> = SidebarLeafEntry<T> | SidebarGroupEntry<T>;

export type AppliedSidebarLayout<T> = {
  /** Full tree in display order, hidden leaves included. */
  all: SidebarEntry<T>[];
  /** Tree without hidden leaves. Groups are kept even when empty so they can be edited. */
  visible: SidebarEntry<T>[];
  /** Hidden leaves, in display order. */
  hidden: SidebarLeafEntry<T>[];
};

/** Where a leaf or group sits: top level (`parentId: null`) or inside a group. */
export type SidebarEntryPosition = { parentId: string | null; index: number };

const buildTree = <T extends { id: string }>(items: readonly T[], layout: SidebarLayout): SidebarEntry<T>[] => {
  const itemsById = new Map(items.map(item => [item.id, item] as const));
  const linksById = new Map(layout.links.map(link => [link.id, link] as const));
  const groupsById = new Map(layout.groups.map(group => [group.id, group] as const));
  const placed = new Set<string>();

  const leafFor = (id: string): SidebarLeafEntry<T> | null => {
    if (placed.has(id)) {
      return null;
    }

    const item = itemsById.get(id);
    if (item) {
      placed.add(id);
      return { kind: 'item', id, item };
    }

    const link = linksById.get(id);
    if (link) {
      placed.add(id);
      return { kind: 'link', id, link };
    }

    return null;
  };

  const groupFor = (id: string): SidebarGroupEntry<T> | null => {
    const group = groupsById.get(id);
    if (!group || placed.has(id)) {
      return null;
    }

    placed.add(id);
    const children: SidebarLeafEntry<T>[] = [];
    for (const childId of group.children) {
      const leaf = leafFor(childId);
      if (leaf) {
        children.push(leaf);
      }
    }

    return { kind: 'group', id, group, children };
  };

  const tree: SidebarEntry<T>[] = [];
  for (const id of layout.order) {
    const entry = isGroupId(id) ? groupFor(id) : leafFor(id);
    if (entry) {
      tree.push(entry);
    }
  }

  // Groups the order never mentions still own their children; place them before loose entries.
  for (const group of layout.groups) {
    const entry = groupFor(group.id);
    if (entry) {
      tree.push(entry);
    }
  }

  for (const item of items) {
    const entry = leafFor(item.id);
    if (entry) {
      tree.push(entry);
    }
  }

  for (const link of layout.links) {
    const entry = leafFor(link.id);
    if (entry) {
      tree.push(entry);
    }
  }

  return tree;
};

const itemIdsOf = <T>(tree: readonly SidebarEntry<T>[]): string[] => {
  const ids: string[] = [];
  for (const entry of tree) {
    if (entry.kind === 'item') {
      ids.push(entry.id);
    } else if (entry.kind === 'group') {
      for (const child of entry.children) {
        if (child.kind === 'item') {
          ids.push(child.id);
        }
      }
    }
  }

  return ids;
};

/** Writes a tree back into a layout. Group memberships of entries currently out of reach are kept. */
const serializeTree = <T extends { id: string }>(
  tree: readonly SidebarEntry<T>[],
  layout: SidebarLayout,
  known: ReadonlySet<string>,
): SidebarLayout => {
  const previousGroups = new Map(layout.groups.map(group => [group.id, group] as const));

  const groups: SidebarLayoutGroup[] = [];
  for (const entry of tree) {
    if (entry.kind !== 'group') {
      continue;
    }

    const childIds = entry.children.map(child => child.id);
    const ghosts = (previousGroups.get(entry.id)?.children ?? []).filter(id => !known.has(id) && !childIds.includes(id));
    groups.push({ id: entry.id, label: entry.group.label, children: [...childIds, ...ghosts] });
  }

  return {
    order: tree.map(entry => entry.id),
    hidden: layout.hidden,
    groups,
    links: layout.links,
  };
};

const knownIds = <T extends { id: string }>(items: readonly T[], layout: SidebarLayout): Set<string> =>
  new Set([...items.map(item => item.id), ...layout.links.map(link => link.id), ...layout.groups.map(group => group.id)]);

/**
 * Resolves the layout against the entries the person may see. Entries missing from the layout
 * keep their default relative position after the ordered ones, so new modules stay discoverable.
 * If the layout would hide every workspace entry, hiding is ignored and everything is shown.
 */
export const applySidebarLayout = <T extends { id: string }>(
  items: readonly T[],
  layout: SidebarLayout,
): AppliedSidebarLayout<T> => {
  const all = buildTree(items, layout);
  const hiddenIds = new Set(layout.hidden);
  const anyItemVisible = itemIdsOf(all).some(id => !hiddenIds.has(id));
  if (!anyItemVisible) {
    hiddenIds.clear();
  }

  const hidden: SidebarLeafEntry<T>[] = [];
  const visible: SidebarEntry<T>[] = [];
  for (const entry of all) {
    if (entry.kind === 'group') {
      const children: SidebarLeafEntry<T>[] = [];
      for (const child of entry.children) {
        if (hiddenIds.has(child.id)) {
          hidden.push(child);
        } else {
          children.push(child);
        }
      }
      visible.push({ ...entry, children });
    } else if (hiddenIds.has(entry.id)) {
      hidden.push(entry);
    } else {
      visible.push(entry);
    }
  }

  return { all, visible, hidden };
};

const locate = <T>(tree: readonly SidebarEntry<T>[], id: string): SidebarEntryPosition | null => {
  for (let index = 0; index < tree.length; index += 1) {
    const entry = tree[index];
    if (entry.id === id) {
      return { parentId: null, index };
    }

    if (entry.kind === 'group') {
      const childIndex = entry.children.findIndex(child => child.id === id);
      if (childIndex >= 0) {
        return { parentId: entry.id, index: childIndex };
      }
    }
  }

  return null;
};

const cloneTree = <T>(tree: readonly SidebarEntry<T>[]): SidebarEntry<T>[] =>
  tree.map(entry => (entry.kind === 'group' ? { ...entry, children: [...entry.children] } : entry));

/**
 * Moves an entry to a new position. `target.index` is relative to the full tree (hidden leaves
 * included) and is read before the entry is removed from its old slot. Groups only live at the
 * top level; moving a group into a group puts it before that group instead.
 */
export const moveSidebarEntry = <T extends { id: string }>(
  items: readonly T[],
  layout: SidebarLayout,
  id: string,
  target: SidebarEntryPosition,
): SidebarLayout => {
  const tree = cloneTree(buildTree(items, layout));
  const source = locate(tree, id);
  if (!source) {
    return layout;
  }

  const moving = source.parentId === null
    ? tree[source.index]
    : (tree.find(entry => entry.id === source.parentId) as SidebarGroupEntry<T>).children[source.index];

  let destination = target;
  if (moving.kind === 'group' && destination.parentId !== null) {
    const parentIndex = tree.findIndex(entry => entry.id === destination.parentId);
    destination = { parentId: null, index: parentIndex < 0 ? tree.length : parentIndex };
  }

  if (destination.parentId !== null) {
    const parent = tree.find(entry => entry.id === destination.parentId);
    if (!parent || parent.kind !== 'group' || parent.id === id) {
      return layout;
    }
  }

  // Remove from the old slot, adjusting the destination index when both share a list.
  if (source.parentId === null) {
    tree.splice(source.index, 1);
    if (destination.parentId === null && destination.index > source.index) {
      destination = { ...destination, index: destination.index - 1 };
    }
  } else {
    const parent = tree.find(entry => entry.id === source.parentId) as SidebarGroupEntry<T>;
    parent.children.splice(source.index, 1);
    if (destination.parentId === source.parentId && destination.index > source.index) {
      destination = { ...destination, index: destination.index - 1 };
    }
  }

  if (destination.parentId === null) {
    const index = Math.max(0, Math.min(destination.index, tree.length));
    tree.splice(index, 0, moving);
  } else {
    const parent = tree.find(entry => entry.id === destination.parentId) as SidebarGroupEntry<T>;
    const index = Math.max(0, Math.min(destination.index, parent.children.length));
    parent.children.splice(index, 0, moving as SidebarLeafEntry<T>);
  }

  return serializeTree(tree, layout, knownIds(items, layout));
};

/** Position of an entry in the full tree, or null when the layout does not know it. */
export const locateSidebarEntry = <T extends { id: string }>(
  items: readonly T[],
  layout: SidebarLayout,
  id: string,
): SidebarEntryPosition | null => locate(buildTree(items, layout), id);

export type SidebarMoveTarget =
  | { kind: 'before'; anchorId: string }
  | { kind: 'after'; anchorId: string }
  | { kind: 'into'; groupId: string };

/** Moves an entry relative to another entry, or appends it to a group. */
export const moveSidebarEntryTo = <T extends { id: string }>(
  items: readonly T[],
  layout: SidebarLayout,
  id: string,
  target: SidebarMoveTarget,
): SidebarLayout => {
  if (target.kind === 'into') {
    if (isGroupId(id)) {
      return layout;
    }

    const tree = buildTree(items, layout);
    const group = tree.find(entry => entry.id === target.groupId);
    if (!group || group.kind !== 'group') {
      return layout;
    }

    return moveSidebarEntry(items, layout, id, { parentId: group.id, index: group.children.length });
  }

  if (target.anchorId === id) {
    return layout;
  }

  const anchor = locateSidebarEntry(items, layout, target.anchorId);
  if (!anchor) {
    return layout;
  }

  return moveSidebarEntry(items, layout, id, {
    parentId: anchor.parentId,
    index: target.kind === 'before' ? anchor.index : anchor.index + 1,
  });
};

/** Hides or shows one workspace entry or link. Refuses to hide the last visible workspace entry. */
export const setSidebarItemHidden = <T extends { id: string }>(
  items: readonly T[],
  layout: SidebarLayout,
  id: string,
  hidden: boolean,
): SidebarLayout => {
  if (isGroupId(id)) {
    return layout;
  }

  const tree = buildTree(items, layout);
  const known = knownIds(items, layout);
  const hiddenIds = new Set(layout.hidden);

  if (hidden) {
    hiddenIds.add(id);
  } else {
    hiddenIds.delete(id);
  }

  const itemIds = itemIdsOf(tree);
  if (itemIds.length > 0 && itemIds.every(itemId => hiddenIds.has(itemId))) {
    return layout;
  }

  const serialized = serializeTree(tree, layout, known);
  return {
    ...serialized,
    hidden: [
      ...Array.from(known).filter(knownId => hiddenIds.has(knownId) && !isGroupId(knownId)),
      // Keep hidden flags for entries that are currently out of reach (e.g. plan downgrade).
      ...layout.hidden.filter(entry => !known.has(entry)),
    ],
  };
};

export const createSidebarGroupId = (): string => randomId(GROUP_ID_PREFIX);

/** Appends an empty group. The caller supplies the id (see `createSidebarGroupId`) so it can focus it afterwards. */
export const addSidebarGroup = <T extends { id: string }>(
  items: readonly T[],
  layout: SidebarLayout,
  id: string,
  label: string,
): SidebarLayout => {
  if (!isGroupId(id) || layout.groups.some(group => group.id === id)) {
    return layout;
  }

  const tree = buildTree(items, layout);
  const group: SidebarLayoutGroup = { id, label: normalizeLabel(label, MAX_GROUP_LABEL_LENGTH) ?? '', children: [] };
  return serializeTree([...tree, { kind: 'group', id, group, children: [] }], layout, knownIds(items, layout));
};

export const renameSidebarGroup = (layout: SidebarLayout, id: string, label: string): SidebarLayout => {
  const normalized = normalizeLabel(label, MAX_GROUP_LABEL_LENGTH) ?? '';
  if (!layout.groups.some(group => group.id === id)) {
    return layout;
  }

  return {
    ...layout,
    groups: layout.groups.map(group => (group.id === id ? { ...group, label: normalized } : group)),
  };
};

/** Removes a group; its entries take the group's place at the top level. */
export const removeSidebarGroup = <T extends { id: string }>(
  items: readonly T[],
  layout: SidebarLayout,
  id: string,
): SidebarLayout => {
  const tree = buildTree(items, layout);
  const index = tree.findIndex(entry => entry.id === id);
  if (index < 0) {
    return layout;
  }

  const group = tree[index] as SidebarGroupEntry<T>;
  const next = [...tree.slice(0, index), ...group.children, ...tree.slice(index + 1)];
  const serialized = serializeTree(next, layout, knownIds(items, layout));
  return { ...serialized, groups: serialized.groups.filter(entry => entry.id !== id) };
};

export const addSidebarLink = <T extends { id: string }>(
  items: readonly T[],
  layout: SidebarLayout,
  label: string,
  url: string,
): SidebarLayout => {
  const normalizedUrl = normalizeLinkUrl(url);
  if (!normalizedUrl) {
    return layout;
  }

  const id = randomId(LINK_ID_PREFIX);
  const link: SidebarLayoutLink = { id, label: normalizeLabel(label, MAX_LINK_LABEL_LENGTH) ?? normalizedUrl, url: normalizedUrl };
  const withLink = { ...layout, links: [...layout.links, link] };
  const tree = buildTree(items, withLink);
  return serializeTree(tree, withLink, knownIds(items, withLink));
};

export const removeSidebarLink = <T extends { id: string }>(
  items: readonly T[],
  layout: SidebarLayout,
  id: string,
): SidebarLayout => {
  if (!layout.links.some(link => link.id === id)) {
    return layout;
  }

  const withoutLink: SidebarLayout = {
    ...layout,
    links: layout.links.filter(link => link.id !== id),
    hidden: layout.hidden.filter(entry => entry !== id),
    groups: layout.groups.map(group => ({ ...group, children: group.children.filter(child => child !== id) })),
  };
  const tree = buildTree(items, withoutLink);
  return serializeTree(tree, withoutLink, knownIds(items, withoutLink));
};
